import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChannelIcon } from "@/components/common/ChannelIcon";
import { cn } from "@/lib/utils";
import { Plus, MoreVertical, Send, Search, Grid3x3, List, Linkedin, Mail, MessageCircle, MessageSquare, Phone, ShieldCheck, ArrowRight } from 'lucide-react';
import { useFreshMode, Day1Empty } from "@/components/empty/Day1Empty";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { outreachApi } from "@/lib/api";
import { statusLabel, statusClass } from "@/lib/campaignWorkflow";

const channelIconMap = { email: Mail, linkedin: Linkedin, whatsapp: MessageCircle, sms: MessageSquare, voice: Phone };

const OUTBOUND_KPI_SKELETON_KEYS = ["outbound-kpi-a", "outbound-kpi-b", "outbound-kpi-c", "outbound-kpi-d"];
const ACCOUNT_HEALTH_SKELETON_KEYS = ["health-a", "health-b", "health-c", "health-d", "health-e"];

function accountHealthStatus(pct) {
  if (pct >= 90) return { label: "Slowing down", barCls: "bg-rose-500", labelCls: "text-rose-700" };
  if (pct >= 70) return { label: "Pacing well", barCls: "bg-amber-500", labelCls: "text-amber-700" };
  return { label: "Safe", barCls: "bg-indigo-500", labelCls: "text-indigo-700" };
}

function renderChannelPerformanceChart(loading, chartData) {
  if (loading) return <Skeleton className="h-full w-full rounded" />;
  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-slate-400">
        No outbound activity yet in this window.
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} barGap={2} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
        <XAxis dataKey="ch" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <YAxis width={32} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8 }} />
        <Bar dataKey="sent" fill="#4F46E5" radius={[3, 3, 0, 0]} />
        <Bar dataKey="replies" fill="#10B981" radius={[3, 3, 0, 0]} />
        <Bar dataKey="meetings" fill="#F59E0B" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ===================== A1. Outbound Dashboard ===================== */
export function OutboundDashboard() {
  const navigate = useNavigate();
  const fresh = useFreshMode();
  const [summary, setSummary] = useState(null);
  const [summaryErr, setSummaryErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const [campaignRows, setCampaignRows] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);

  useEffect(() => {
    queueMicrotask(async () => {
      try {
        const r = await outreachApi.summary();
        setSummary(r);
      } catch (e) {
        setSummaryErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    queueMicrotask(async () => {
      try {
        const r = await outreachApi.listCampaigns();
        setCampaignRows(r.campaigns || []);
      } catch {
        setCampaignRows([]);
      } finally {
        setCampaignsLoading(false);
      }
    });
  }, []);

  if (fresh) return (
    <Day1Empty
      Icon={Send}
      title="Start your first outreach"
      body="Create a campaign and build a multi-channel sequence."
      actions={[
        { label: "Create campaign", onClick: () => navigate("/outreach/campaigns/new"), testId: "outreach-empty-build" },
        { label: "View campaigns", onClick: () => navigate("/outreach/campaigns"), testId: "outreach-empty-maya" },
      ]}
    />
  );

  // Sprint 7.5: KPIs / chart / account-health are now backend-driven from
  // real `messages` + `safety` rows. The "Active sequences" table below
  // remains on mock data â€” the sequence builder lands in Sprint 8+.
  const kpis = summary?.kpis || [];
  const chartData = (summary?.channels || []).map((c) => ({
    ch: c.channel_label, sent: c.sent, replies: c.replies, meetings: c.meetings,
  }));
  const accountHealth = summary?.account_health || [];
  const winLabel = summary ? `last ${summary.window_days}d` : "last 14d";

  return (
    <div className="flex flex-col h-full min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
      <PageHeader title="Outreach" description="Sequences Maya is running on your accounts — channel-aware and account-safe."
        actions={<Button size="sm" className="h-10 sm:h-8 w-full sm:w-auto" onClick={() => navigate("/outreach/campaigns/new")} data-testid="goto-campaigns"><Plus size={14} /> New campaign</Button>} />
      <div className="p-4 sm:p-6 space-y-4 min-w-0" data-testid="outreach-dashboard">

        {summaryErr && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-700 break-words" data-testid="outreach-error">
            Couldn&apos;t load outreach summary: {summaryErr}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="outreach-kpis">
          {loading && OUTBOUND_KPI_SKELETON_KEYS.map((key) => <Skeleton key={key} className="h-[88px] rounded-lg" />)}
          {!loading && kpis.map((kpi) => (
            <div key={kpi.k} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid={`outbound-kpi-${kpi.k}`}>
              <div className="text-[10.5px] font-medium uppercase tracking-wider text-slate-500 truncate">{kpi.label}</div>
              <div className="mt-1 text-[20px] sm:text-[24px] font-semibold tracking-tight text-slate-900 tabular-nums">{kpi.value}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">{kpi.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-w-0">
          <div className="lg:col-span-8 rounded-lg border border-slate-200 bg-white p-3 sm:p-4 min-w-0">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <div className="text-[13px] font-semibold text-slate-900">Channel performance — {winLabel}</div>
              <div className="text-[10.5px] text-slate-500">sent · replies · responding contacts</div>
            </div>
            <div className="mt-3 h-48 sm:h-60 w-full min-w-0" data-testid="outreach-channel-chart">
              {renderChannelPerformanceChart(loading, chartData)}
            </div>
          </div>
          <div className="lg:col-span-4 rounded-lg border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid="account-health-card">
            <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900"><ShieldCheck size={14} className="text-emerald-600 shrink-0" /> Account health</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">
              Today&apos;s actual send count vs your <span className="font-medium">{accountHealth[0]?.preset || "normal"}</span>-preset ceiling.
            </div>
            <div className="mt-3 space-y-2.5">
              {loading && ACCOUNT_HEALTH_SKELETON_KEYS.map((key) => <Skeleton key={key} className="h-7 rounded" />)}
              {!loading && accountHealth.map((a) => {
                const pct = a.cap > 0 ? (a.today / a.cap) * 100 : 0;
                const status = accountHealthStatus(pct);
                return (
                  <div key={a.account} data-testid={`account-health-${a.channel}`} className="min-w-0">
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="font-medium text-slate-700 truncate min-w-0">{a.account}</span>
                      <span className={cn("font-semibold shrink-0", status.labelCls)}>{status.label}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={cn("h-full rounded-full", status.barCls)} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-[10.5px] tabular-nums text-slate-500 shrink-0">{a.today}/{a.cap}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={() => navigate("/outreach/campaigns/new")} className="mt-3 text-[10.5px] text-indigo-700 hover:underline inline-flex items-center gap-1" data-testid="manage-pacing-link">Create campaign <ArrowRight size={9} /></button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden min-w-0">
          <div className="px-3 sm:px-4 py-3 border-b border-slate-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[13px] font-semibold text-slate-900">Your campaigns</div>
            <Button asChild variant="ghost" size="sm" className="h-9 sm:h-8 text-[11.5px] w-full sm:w-auto justify-center"><Link to="/outreach/campaigns">View all <ArrowRight size={11} /></Link></Button>
          </div>
          {campaignsLoading && <div className="p-4"><Skeleton className="h-24 w-full" /></div>}
          {!campaignsLoading && campaignRows.length === 0 && (
            <div className="p-6 sm:p-8 text-center text-[12.5px] text-slate-500">
              No campaigns yet.{" "}
              <button type="button" className="text-indigo-700 hover:underline" onClick={() => navigate("/outreach/campaigns/new")}>
                Create your first campaign
              </button>
            </div>
          )}
          {!campaignsLoading && campaignRows.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[12.5px]">
            <thead className="bg-slate-50/60"><tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2 font-medium">Campaign</th><th className="px-4 py-2 font-medium">Channels</th><th className="px-4 py-2 font-medium text-right">Prospects</th><th className="px-4 py-2 font-medium text-right">Reply</th><th className="px-4 py-2 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {campaignRows.slice(0, 6).map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`outbound-seq-row-${c.id}`}>
                  <td className="px-4 py-2.5">
                    <button type="button" onClick={() => navigate(`/outreach/campaigns/${c.id}/edit`)} className="text-left w-full bg-transparent border-0 p-0 cursor-pointer">
                      <div className="font-medium text-slate-900">{c.name}</div><div className="text-[10.5px] text-slate-500">{c.owner}</div>
                    </button>
                  </td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">{(c.channels || []).map(ch => <ChannelIcon key={ch} channel={ch} size={12} />)}</div></td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{c.prospects || 0}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700 font-medium">{c.reply_rate || 0}%</td>
                  <td className="px-4 py-2.5"><span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", statusClass(c.status))}>{statusLabel(c.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== A2. Campaigns Grid ===================== */
export function OutboundCampaigns() {
  const navigate = useNavigate();
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    queueMicrotask(async () => {
      try {
        const r = await outreachApi.listCampaigns();
        setCampaigns(r.campaigns || []);
      } catch (e) {
        toast.error(e.message || "Failed to load campaigns");
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const list = campaigns.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex flex-col h-full min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
      <PageHeader title="Campaigns" description={`${campaigns.length} campaign${campaigns.length === 1 ? "" : "s"}`} breadcrumbs={["Outreach", "Campaigns"]}
        actions={<>
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
            <button type="button" onClick={() => setView("grid")} className={cn("px-2.5 py-2 sm:py-1 rounded min-h-9 sm:min-h-0", view === "grid" ? "bg-white shadow-sm" : "")} data-testid="view-grid" aria-label="Grid view"><Grid3x3 size={13} /></button>
            <button type="button" onClick={() => setView("list")} className={cn("px-2.5 py-2 sm:py-1 rounded min-h-9 sm:min-h-0", view === "list" ? "bg-white shadow-sm" : "")} data-testid="view-list" aria-label="List view"><List size={13} /></button>
          </div>
          <Button size="sm" className="h-10 sm:h-8 w-full sm:w-auto" onClick={() => navigate("/outreach/campaigns/new")} data-testid="new-campaign-btn"><Plus size={14} /> New campaign</Button>
        </>} />
      <div className="px-4 sm:px-6 py-3 bg-white border-b border-slate-200 shrink-0">
        <div className="relative max-w-md w-full">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search campaigns…" className="h-11 sm:h-9 pl-7 w-full min-w-0" />
        </div>
      </div>
      <div className="p-4 sm:p-6 min-w-0">
        {loading && <Skeleton className="h-40 w-full" />}
        {!loading && list.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 sm:p-12 text-center">
            <div className="text-[14px] font-medium text-slate-900">No campaigns yet</div>
            <p className="text-[12.5px] text-slate-500 mt-1">Create your first outreach campaign to get started.</p>
            <Button className="mt-4 h-10 sm:h-9 w-full sm:w-auto" size="sm" onClick={() => navigate("/outreach/campaigns/new")}>Create campaign</Button>
          </div>
        )}
        {!loading && view === "grid" && list.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((c) => (
              <button type="button" key={c.id} onClick={() => navigate(`/outreach/campaigns/${c.id}/edit`)} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm cursor-pointer text-left w-full min-w-0" data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between gap-2"><div className="text-[13.5px] font-semibold text-slate-900 leading-tight line-clamp-2 min-w-0">{c.name}</div><MoreVertical size={14} className="text-slate-400 shrink-0" /></div>
                <div className="mt-2 text-[11px] text-slate-500">{c.owner}</div>
                <div className="mt-3 flex items-center gap-1">{(c.channels || []).map(ch => <ChannelIcon key={ch} channel={ch} size={12} />)}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div><div className="text-slate-500">Prospects</div><div className="font-semibold text-slate-900">{c.prospects || 0}</div></div>
                  <div><div className="text-slate-500">Reply</div><div className="font-semibold text-emerald-700">{c.reply_rate || 0}%</div></div>
                  <div><div className="text-slate-500">Meetings</div><div className="font-semibold text-slate-900">{c.meetings || 0}</div></div>
                </div>
                <div className="mt-3 border-t border-slate-100 pt-2 flex items-center justify-between gap-2">
                  <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", statusClass(c.status))}>{statusLabel(c.status)}</span>
                  <span className="text-[10.5px] text-slate-500 truncate">{c.last_activity || "—"}</span>
                </div>
              </button>
            ))}
          </div>
        )}
        {!loading && view === "list" && list.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full min-w-[640px] text-[12.5px]">
              <thead className="bg-slate-50/60"><tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500"><th className="px-4 py-2 font-medium">Campaign</th><th className="px-4 py-2 font-medium">Owner</th><th className="px-4 py-2 font-medium">Channels</th><th className="px-4 py-2 font-medium text-right">Prospects</th><th className="px-4 py-2 font-medium text-right">Reply</th><th className="px-4 py-2 font-medium">Status</th></tr></thead>
              <tbody>{list.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                  <td className="px-4 py-2.5">
                    <button type="button" onClick={() => navigate(`/outreach/campaigns/${c.id}/edit`)} className="font-medium text-slate-900 text-left w-full bg-transparent border-0 p-0 cursor-pointer">{c.name}</button>
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{c.owner}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">{(c.channels || []).map(ch => <ChannelIcon key={ch} channel={ch} size={12} />)}</div></td>
                  <td className="px-4 py-2.5 text-right">{c.prospects || 0}</td><td className="px-4 py-2.5 text-right text-emerald-700">{c.reply_rate || 0}%</td>
                  <td className="px-4 py-2.5"><span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", statusClass(c.status))}>{statusLabel(c.status)}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== A3. Pacing & Safety (shared tab) ===================== */
export { PacingSafetyTab } from "@/pages/outbound/PacingSafetyTab";


export { CampaignEditor as SequenceBuilder } from "@/pages/outbound/CampaignEditor";

function ChannelStub({ title, channel }) {
  return (
    <div className="flex flex-col h-full overflow-y-auto scrollbar-thin p-6">
      <PageHeader title={title} breadcrumbs={["Outreach", channel]} />
      <div className="rounded-lg border border-dashed border-slate-200 p-12 text-center text-[13px] text-slate-500">
        Channel-specific outreach tools are managed through campaigns.{" "}
        <a href="/outreach/campaigns" className="text-indigo-700 hover:underline">Go to Campaigns</a>
      </div>
    </div>
  );
}

ChannelStub.propTypes = {
  title: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
};

export function OutboundLinkedIn() { return <ChannelStub title="LinkedIn outreach" channel="LinkedIn" />; }
export function OutboundEmail() { return <ChannelStub title="Email outreach" channel="Email" />; }
export function OutboundWhatsAppOut() { return <ChannelStub title="WhatsApp outreach" channel="WhatsApp" />; }
export function OutboundSmsOut() { return <ChannelStub title="SMS outreach" channel="SMS" />; }
export function OutboundVoiceOut() { return <ChannelStub title="AI voice calls" channel="Voice" />; }
